import ApplicationModel, { attr } from '../ApplicationModel';
import { type BookmarksFeatureBoardAttributes } from './generated/BookmarksFeatureBoardAttributes';
import { model } from '../modelBuilder';
import CardLayout from './CardLayout';
import { FeatureBoardColumnBy, FeatureBoardGroupBy } from '../Enums';
import Fragment from '../Fragment';

interface BookmarksFeatureBoard extends BookmarksFeatureBoardAttributes {}

@model
class BookmarksFeatureBoard extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksFeatureBoard' = 'BookmarksFeatureBoard';
  get typename(): 'BookmarksFeatureBoard' {
    return 'BookmarksFeatureBoard';
  }

  static internalTypename: 'Bookmarks::FeatureBoardV2' =
    'Bookmarks::FeatureBoardV2';
  get internalTypename(): 'Bookmarks::FeatureBoardV2' {
    return 'Bookmarks::FeatureBoardV2';
  }

  static bookmarkName = 'Bookmarks::FeatureBoard';

  static inputType = 'FeatureBoardAttributes!';

  static fields = {
    columnBy: attr({ writable: true, readable: true }),
    groupBy: attr({ writable: true, readable: true }),
    id: attr({ readable: true }),
    name: attr({ readable: true }),
    projectId: attr({ readable: true }),
    selectedAssignedToUserIds: attr({ writable: true, readable: false }),
    selectedReleaseIds: attr({ writable: true, readable: false }),
    selectedWorkflowStatusIds: attr({ writable: true, readable: false }),
    showCollapsedView: attr({ writable: true, readable: true }),
    cardLayout: attr.belongsTo({
      writeShape: [
        'epicRecordLayout',
        'featureRecordLayout',
        'ideaRecordLayout',
        'initiativeRecordLayout',
        'releaseRecordLayout',
        'releasePhaseRecordLayout',
        'requirementRecordLayout',
        'taskRecordLayout',
        'goalRecordLayout',
        'keyResultRecordLayout',
      ],
      readable: true,
      typename: 'CardLayout',
    }),
    defaultCardLayout: attr.belongsTo({
      readable: true,
      typename: 'CardLayout',
    }),
    features: attr.belongsTo({
      args: {
        page: 'Int',
        per: 'Int',
        assignedToUserId: 'ID',
        workflowStatusIds: '[ID!]',
        searchQuery: 'String',
      },
      readable: true,
      typename: 'FeaturePage',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    columnSelectOptions: attr.hasMany({ readable: true }),
    groupSelectOptions: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  get activeCardLayout() {
    const { cardLayout, defaultCardLayout } = this;

    if (cardLayout && defaultCardLayout) {
      return CardLayout.buildActiveCardLayout({
        cardLayout,
        defaultCardLayout,
      });
    }
  }

  get cardView() {
    return this.showCollapsedView
      ? this.activeCardLayout?.featureRecordLayout?.collapsed
      : this.activeCardLayout?.featureRecordLayout?.expanded;
  }

  async updateGroupSelection(selectedIds: RecordId[]) {
    let mutationField = '';
    switch (this.groupBy) {
      case FeatureBoardGroupBy.ASSIGNEE.value:
        mutationField = 'selectedAssignedToUserIds';
        break;
    }

    if (mutationField) {
      await this.mutate(
        new Fragment('updateBookmarksFeatureBoard')
          .argument('id', this.id)
          .argument(
            'attributes',
            {
              [mutationField]: selectedIds,
            },
            'FeatureBoardAttributes!'
          )
      );
    }
  }

  async updateColumnSelection(selectedIds: RecordId[]) {
    let mutationField = '';
    switch (this.columnBy) {
      case FeatureBoardColumnBy.RELEASE.value:
        mutationField = 'selectedReleaseIds';
        break;
      case FeatureBoardColumnBy.WORKFLOW_STATUS.value:
        mutationField = 'selectedWorkflowStatusIds';
        break;
    }

    if (mutationField) {
      await this.mutate(
        new Fragment('updateBookmarksFeatureBoard')
          .argument('id', this.id)
          .argument(
            'attributes',
            {
              [mutationField]: selectedIds,
            },
            'FeatureBoardAttributes!'
          )
      );
    }
  }
}

export default BookmarksFeatureBoard;
